import React, { memo, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  Icons,
} from '@ublend-npm/aulaui-core';

import { getStarredSpaceColors } from '../../colors';
import SpaceDuration from './SpaceDuration';

export interface Space {
  id: string;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
}

interface SpaceItemProps {
  space: Space;
  isStarred: boolean;
  onStarToggle: (id: string) => void;
}

const SpaceItem = ({ space, isStarred, onStarToggle }: SpaceItemProps) => {
  const { palette } = useTheme();
  const colors = useMemo(() => getStarredSpaceColors(palette), [palette]);

  const handleStarToggle = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onStarToggle(space.id);
    },
    [onStarToggle, space.id]
  );

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={1.5}
      borderRadius={2}
      sx={{
        minWidth: 0,
        width: '100%',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        flex={1}
        sx={{ minWidth: 0 }}
      >
        <Icons.SpaceImageIcon mode={palette.mode} />

        <Box flex={1} sx={{ minWidth: 0, pr: 1 }}>
          <Typography
            variant="body1"
            title={space.name}
            sx={{
              fontWeight: 400,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {space.name}
          </Typography>

          <Typography variant="body2" color={colors.textSecondary} noWrap>
            <SpaceDuration
              startDate={space.startDate}
              endDate={space.endDate}
              gap={8}
            />
          </Typography>
        </Box>
      </Box>

      <IconButton
        onClick={handleStarToggle}
        aria-label={`${
          isStarred ? 'Remove from starred spaces' : 'Add to starred spaces'
        }: ${space.name}`}
        aria-pressed={isStarred}
        size="small"
        sx={{
          color: colors.starIcon,
          flexShrink: 0,
        }}
      >
        {isStarred ? (
          <Icons.Star sx={{ width: 20, height: 19 }} />
        ) : (
          <Icons.StarBorder sx={{ width: 20, height: 19 }} />
        )}
      </IconButton>
    </Box>
  );
};

export default memo(SpaceItem);
